// src/components/AdminSubdomainGuard.jsx
// Locks down the app when accessed via admin.thescope180.com
// Only admin role users can access — everyone else sees an auth gate.

import React from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";

const ADMIN_HOSTNAMES = ["admin.thescope180.com"];
const EBOOKS_HOSTNAMES = ["ebooks.thescope180.com"];

/**
 * Returns true if the current hostname is the admin subdomain.
 */
export function isAdminSubdomain() {
  return ADMIN_HOSTNAMES.includes(window.location.hostname);
}

/**
 * Returns true if the current hostname is the eBooks store subdomain
 * (ebooks.thescope180.com). The store renders standalone there.
 */
export function isEbooksSubdomain() {
  return EBOOKS_HOSTNAMES.includes(window.location.hostname);
}

/**
 * Wraps the app to enforce admin-only access on the admin subdomain.
 * - Not logged in → redirects to /login
 * - Logged in but not admin → shows "not authorized"
 * - Admin → renders children (the normal app)
 */
export default function AdminSubdomainGuard({ children }) {
  const { currentUser, loading, roles } = useAuth();

  // Don't guard on the main domain
  if (!isAdminSubdomain()) return children;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-400">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = roles?.some(
    (r) => String(r).toLowerCase().trim() === "admin"
  );

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">&#128274;</div>
          <h1 className="text-2xl font-black text-white mb-2">Access Denied</h1>
          <p className="text-sm text-gray-400 mb-6">
            This portal is restricted to authorized administrators only.
          </p>
          <a
            href="https://www.thescope180.com"
            className="inline-block px-6 py-2.5 rounded-full bg-yellow-400 text-black text-sm font-bold hover:bg-yellow-300 transition"
          >
            Go to TheScope180.com
          </a>
        </div>
      </div>
    );
  }

  return children;
}
